// Raport diagnostyczny z proby - do skopiowania i przekazania dalej.
//
// Ma zawierac WSZYSTKO, co potrzebne do odtworzenia przebiegu bez zgadywania:
// identyfikator i parametry trasy, konstrukcje (blueprint) oraz przebieg proby
// (probki co 0,1 s). Czysta funkcja - latwo ja przetestowac.

import type { ChallengeDefinition } from '../challenges/step';
import type { RobotBlueprint } from '../domain/types';
import type { TrialSample } from '../physics/simulation';

export const DEBUG_REPORT_SCHEMA = 1;

export interface DebugReportInput {
  challenge: ChallengeDefinition;
  blueprint: RobotBlueprint;
  outcome: string;
  reason: string;
  elapsedSec: number;
  samples: readonly TrialSample[];
  sampleEverySec: number;
  brakeTorqueFactor: number;
  userAgent?: string;
  /** Znacznik czasu (ISO). Podawany z zewnatrz, zeby funkcja byla deterministyczna. */
  nowIso?: string;
}

export interface DebugReport {
  schema: number;
  app: 'robot-lab';
  generatedAt: string;
  userAgent: string;
  outcome: string;
  reason: string;
  elapsedSec: number;
  brakeTorqueFactor: number;
  sampleEverySec: number;
  challenge: {
    id: string;
    label: string;
    finishX: number;
    platformTopY: number;
    groundTopY: number;
    budgetPln: number;
    timeLimitSec: number;
    settleTimeSec: number;
    stuckTimeoutSec: number | null;
    staticBoxes: ChallengeDefinition['staticBoxes'];
    dynamicProps: ChallengeDefinition['dynamicProps'] | null;
    propTargets: ChallengeDefinition['propTargets'] | null;
    outOfBounds: ChallengeDefinition['outOfBounds'];
  };
  final: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    comX: number;
    comY: number;
    angle: number;
    speed: number;
    t: number;
  };
  samples: {
    t: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    comX: number;
    comY: number;
    angle: number;
    speed: number;
  }[];
  blueprint: RobotBlueprint;
}

function round(value: number, digits = 3): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function sampleOf(sample: TrialSample) {
  return {
    t: round(sample.t, 3),
    minX: round(sample.minX),
    maxX: round(sample.maxX),
    minY: round(sample.minY),
    maxY: round(sample.maxY),
    comX: round(sample.comX),
    comY: round(sample.comY),
    angle: round(sample.angle, 4),
    speed: round(sample.speed, 3),
  };
}

/** Buduje raport (obiekt). */
export function buildDebugReportData(input: DebugReportInput): DebugReport {
  const { challenge } = input;
  return {
    schema: DEBUG_REPORT_SCHEMA,
    app: 'robot-lab',
    generatedAt: input.nowIso ?? '',
    userAgent: input.userAgent ?? '',
    outcome: input.outcome,
    reason: input.reason,
    elapsedSec: round(input.elapsedSec, 3),
    brakeTorqueFactor: input.brakeTorqueFactor,
    sampleEverySec: input.sampleEverySec,
    challenge: {
      id: challenge.id,
      label: challenge.label,
      finishX: challenge.finishX,
      platformTopY: challenge.platformTopY,
      groundTopY: challenge.groundTopY,
      budgetPln: challenge.budgetPln,
      timeLimitSec: challenge.timeLimitSec,
      settleTimeSec: challenge.settleTimeSec,
      stuckTimeoutSec: challenge.stuckTimeoutSec,
      staticBoxes: challenge.staticBoxes,
      dynamicProps: challenge.dynamicProps ?? null,
      propTargets: challenge.propTargets ?? null,
      outOfBounds: challenge.outOfBounds,
    },
    final: sampleOf(input.samples[input.samples.length - 1] ?? input.samples[0]!),
    samples: input.samples.map(sampleOf),
    blueprint: input.blueprint,
  };
}

/** Raport jako JSON do wklejenia w zgloszeniu. */
export function buildDebugReportJson(input: DebugReportInput): string {
  return JSON.stringify(buildDebugReportData(input), null, 2);
}
